// RamsDocs marketing copy + brand constants.
// Positioning ported from products/01-rams/site.json, rebranded RamsKit -> RamsDocs.
//
// COMPLIANCE GUARDRAILS (do not break — this is YMYL safety content):
//  - No absolute acceptance/compliance guarantees: never "accepted first time",
//    "guaranteed to pass", "CHAS approved", or unqualified "HSE compliant".
//  - Frame as: AI drafts -> the competent person reviews and approves.
//  - "Built for / ready for principal-contractor review", "designed to reduce
//    rework and rejections" are the safe value claims.

export const SITE = {
  name: "RamsDocs",
  domain: "ramsdocs.co.uk",
  url: "https://ramsdocs.co.uk",
  email: "hello@ramsdocs.co.uk",
  tagline: "Site-specific RAMS drafts for UK trades",
  description:
    "RamsDocs helps UK trades create structured, site-specific RAMS drafts from a specialist safety library. Add the job details, review as the competent person and export a clean PDF for principal-contractor review. Free during early access.",
};

export const HERO = {
  eyebrow: "RAMS for UK trades, tailored to the job",
  headline: "Find or build a site-specific RAMS.",
  sub: "Start from the closest template or build from scratch. Add your site details, answer the risk questions and download a clean draft PDF.",
  cta: "Build a RAMS draft",
  ctaNote: "Free during early access — no card required.",
  // Trust bullets under the hero — output-first framing.
  proofPills: [
    "Site-specific prompts",
    "Competent-person review built in",
    "CDM/HSE-grounded structure",
    "Free during early access",
  ],
};

// Google Ads conversion tracking (account 5838849027; labels from site.json).
export const GOOGLE_ADS = {
  id: "AW-18199893944",
  signupSendTo: "AW-18199893944/nd79COGixLYcELivseZD",
};

// Microsoft Advertising UET tag (Bing). Conversion + remarketing tracking for the
// Microsoft Ads campaigns, which were previously untracked on-site.
export const MICROSOFT_UET = {
  id: "187254631",
};

// North-star positioning line, reused across the page.
export const POSITIONING = "Draft fast. Review properly. Submit with evidence.";

// Shown near every signup/CTA. Keeps the AI-drafts-you-review duty clear (YMYL).
export const DISCLAIMER =
  "RamsDocs helps draft structured RAMS from your job details. It does not replace competent-person review, site-specific judgement or your legal duties.";

// The "you've been here" pain — verbatim voice-of-customer from validation.
export const PAIN = {
  heading: "You won the job. Now they want the paperwork.",
  intro:
    "Every new site wants its own RAMS, and a generic copy-paste is the fastest way to get sent back. RamsDocs starts you from a proper site-specific document instead of a blank Word file.",
  quotes: [
    "Spent the whole evening rewriting last job’s method statement and it still got sent back — wrong site name in two places.",
    "PC rejected our RAMS for ‘vague controls’. Couldn’t start Monday. Lost a day’s labour for four lads.",
    "Every new site wants its own RAMS. I’m a sparky, not a health & safety writer.",
    "We just copy-paste the last one and pray. One day it’s going to bite us.",
  ],
};

export const FEATURES = [
  {
    tag: "01",
    title: "Site-specific, not copy-paste",
    body: "Start from the actual task, then add the site, scope, dates, access constraints and interfaces. The document has to reflect this job, not last month’s project with the name changed.",
  },
  {
    tag: "02",
    title: "Controls with evidence behind them",
    body: "Method steps, hazard prompts, PPE, competence, permits and emergency arrangements sit in the same workflow, so a supervisor can brief the RAMS and a reviewer can follow the controls.",
  },
  {
    tag: "03",
    title: "Review-ready, not overclaimed",
    body: "Structured around CDM 2015 and relevant HSE guidance. RamsDocs drafts; the competent person checks the site-specific detail, signs off and submits for principal-contractor review.",
  },
];

export const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Choose your trade",
    body: "Pick the work your team is doing. RamsDocs loads a trade-specific RAMS starting point with hazards, controls, PPE and method steps.",
  },
  {
    step: "2",
    title: "Configure it live",
    body: "Confirm what applies to this job: working at height, hot works, occupied site, dust, chemicals, lifting, electrical isolation, access and interfaces.",
  },
  {
    step: "3",
    title: "Get it review-ready",
    body: "RamsDocs flags missing safety-critical details and evidence prompts before you review, approve and download for principal-contractor review.",
  },
];

// "No long questionnaire" comparison — old way vs RamsDocs.
export const COMPARISON: { old: string; ramsdocs: string }[] = [
  { old: "Start with a blank Word doc", ramsdocs: "Start with a trade-specific RAMS draft" },
  { old: "Answer every question upfront", ramsdocs: "Configure the visible document live" },
  { old: "Copy-paste last job", ramsdocs: "Built from a specialist RAMS library" },
  { old: "Spot missing details after rejection", ramsdocs: "Pre-submission checker flags gaps" },
];

// Readiness-checker examples for the on-page section (kept generic, no guarantees).
export const READINESS_EXAMPLES = [
  "Work at height selected but access method missing",
  "Hot works selected but permit / fire-watch not confirmed",
  "COSHH trigger detected but substance controls missing",
  "Occupied premises selected but public segregation not included",
  "Site supervisor / competent person missing",
  "Emergency / rescue arrangements need confirmation",
  "Briefing, sign-off or review date missing",
];

// Free during early access (decision 2026-06-04): the feedback loop comes first;
// pricing returns once the product has proven itself with real jobs. Framed as
// "free during early access" so introducing pricing later isn't a take-away.
export const PRICING = {
  note: "Every RAMS draft is free while we build RamsDocs with our first users. No card, no subscription.",
  tiers: [
    {
      name: "Everything included",
      price: "£0",
      period: "/RAMS",
      summary: "Free while we build RamsDocs with our first users.",
      features: [
        "Tailored to your trade, site and job",
        "Ready for principal-contractor review",
        "Clean, branded PDF",
        "Founder-led setup and review support",
        "No card required",
      ],
      cta: "Build a free RAMS draft",
      tierId: "early-access",
      highlighted: true,
    },
  ],
};

// Maps a pricing tier to its analytics event (build brief tracking list).
export const PRICING_EVENT: Record<string, string> = {
  "early-access": "pricing_early_access_clicked",
};

export const FAQS = [
  {
    q: "Will this guarantee my RAMS gets accepted?",
    a: "No — and be wary of anyone who promises that. Acceptance is the principal contractor’s decision. RamsDocs gives you a site-specific document with the hazards and controls a PC expects to see, which is designed to reduce the rework and rejections that come from generic copy-paste documents. You remain the competent person who reviews and signs it off.",
  },
  {
    q: "Is it compliant with CDM 2015 and HSE guidance?",
    a: "Templates are structured around the Construction (Design and Management) Regulations 2015 and reference relevant HSE guidance. They’re a strong starting point, not legal advice — you’re responsible for checking the content is suitable and sufficient for your specific site and work.",
  },
  {
    q: "Who writes the content — is it just AI?",
    a: "AI drafts the document from a structured template; a competent person (you) reviews, adjusts and approves it before it’s used. The templates are built from recognised hazards and control measures for each task, not generated from scratch each time.",
  },
  {
    q: "What does it cost?",
    a: "Nothing right now. Every RAMS is free during early access — no card, no usage cap. We’ll introduce pricing once the product has proven itself with real jobs, and early-access users will get clear notice before anything changes.",
  },
  {
    q: "What if my RAMS gets sent back?",
    a: "During early access, send us the reviewer’s comments and we’ll help you turn them into specific changes. You still remain the competent person who reviews, approves and signs off the updated RAMS.",
  },
  {
    q: "I’m a sole trader — is this overkill?",
    a: "No. Sole traders are exactly who get caught out when a principal contractor asks for a RAMS before a Monday start. One tailored RAMS, free during early access — get what you need for this job and move on.",
  },
  {
    q: "Do you cover my trade?",
    a: "The library covers common UK construction trades and high-risk tasks including work at height, electrical, groundworks, lifting, hot works, COSHH, demolition, finishing trades and general site work. The live builder is narrower during early access; if your task is not live yet, request it and we’ll prioritise it.",
  },
];

// Trade options for the signup form (from site.json signup.fields).
export const SIGNUP_FIELDS = {
  companySize: ["Just me", "2-10", "11-50", "50+"],
  trade: [
    "Scaffolding",
    "Electrical",
    "Groundworks",
    "Roofing",
    "Plumbing & Heating",
    "Demolition",
    "Carpentry / Joinery",
    "Painting & Decorating",
    "Facilities / M&E",
    "Principal Contractor",
    "Other",
  ],
  role: ["Owner / Director", "Site / Contracts Manager", "Health & Safety", "Admin", "Other"],
};
